import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Spin,
  Form,
  Input,
  Button,
  Row,
  Col,
  Space,
  Tooltip,
  Grid,
  message,
  Modal,
  Descriptions,
  Collapse,
  DatePicker,
} from 'antd';
import { useHistory } from 'react-router';
import {
  DeleteOutlined,
  SaveOutlined,
  SearchOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import Table from 'components/Table';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import currencyFormater from 'utils/toCurrency';
import DatosDePago from 'views/Recaudacion/Caja/DatosDePago';
import { getMetodosDePago } from 'api/configuracion';
import { getBancos } from 'api/configuracion/bancos';
import { useSelector } from 'react-redux';
import { getOrdenDePago, postCargosReferenciados, postConsultaFolio } from 'api/recaudacion';
import {
  getRecibo,
  getRecibos,
  getRecibosPDF,
  postFilePagoExterno,
  postReciboConFolioDeOrden,
} from 'api/recaudacion/recibos';
import { getCatalogoDePadrones } from 'api/catalogos';
import { toMoment, toUpper } from 'utils/normalizers';
import padronesToString from 'utils/padronesToString';
import useCorte from 'hooks/useCorte';
import round from 'utils/round';
import { gettersPadronesCajaMap } from 'utils/padrones';
import { padronesModels } from 'utils/permission/models';
import { ActionsNames } from 'utils/permission';
// import debtsFormatter from 'utils/debtsFormatter';
import { ESTADOS_GLOBALES_MAP } from 'utils/estadosGlobales';
import Previewer from 'components/Previewer';
import { getTiposDeReferenciaDePagos } from 'api/catalogos/tiposDeReferenciasDePago';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import Uploader from 'components/Uploader';
import debtsFormatter from 'utils/debtsFormatter';
import { getConvenio, getParcialidades } from 'api/recaudacion/convenios';

const customSections = [
  {
    name: 'consulta_padrones',
    title: 'Consulta de padrones',
    models: padronesModels,
    action: ActionsNames.view,
  },
];

const customActions = [
  {
    name: 'regresar_cargos_version_anterior',
    title: 'Regresar a versión anterior',
    permissions: ['regresar_cargos_version_anterior'],
  },
];
const required = [
  'view_cargo',
  'view_contenttype',
  'view_padron',
  'view_configuraciondeclave',
];

export const permissionConfigConsulta = {
  mainPermission: 'viewcrud_referenciadepago',
  required: [
    ...required,
    'view_recibo',
  ],
  customActions,
  customSections,
};

export const permissionConfig = {
  ...permissionConfigConsulta,
  required,
  customActions: [
    ...customActions,
    {
      name: 'cobrar',
      title: 'Cobrar',
      permissions: [
        'view_corte',
        'add_corte',
        'view_banco',
        'view_catalogodemetododepago',
        'add_recibo',
        'view_recibo',
        'view_catalogodeefectivo',
        'view_usodefolio',
      ],
    },
  ],
};

const RegistroDePagos = ({ esConsulta, permission }) => {
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const permitirCobro = !esConsulta && permission.cobrar;
  const { corte, alerta } = useCorte();
  const entidad = useSelector(({ auth }) => auth.entidad);
  const history = useHistory();
  const screens = Grid.useBreakpoint();
  const [form] = Form.useForm();
  const [formDatosPago] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedMetodo, setSelectedMetodo] = useState(entidad.metodo_de_pago_principal);
  const [metodosPago, setMetodosPago] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [visibleDatosDePago, setVisibleDatosDePago] = useState(false);
  const [datosDePago, setDatosDePago] = useState();
  const debounceRef = useRef(null);
  const searchRef = useRef(null);
  const [descriptionItems, setDescriptionItems] = useState([]);
  const [tiposDeReferenciasDePago, setTiposDeReferenciasDePago] = useState([]);
  const pago_externo = Form.useWatch('pago_externo', formDatosPago);

  const [dataSource, setDataSource] = useState([]);
  const [convenio, setConvenio] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      const [dataMetodosDePago, _bancos, _tiposDeReferencia] = await Promise.all([
        getMetodosDePago(),
        getBancos(),
        getTiposDeReferenciaDePagos(),
        getCatalogoDePadrones(),
      ]);

      if (mounted) {
        const _metodosDePago = dataMetodosDePago.map((e) => {
          if (e.id === 1) return { ...e, prioridad: 3 };
          if (e.id === 4) return { ...e, prioridad: 1 };
          if (e.id === 18) return { ...e, prioridad: 2 };
          return { ...e, prioridad: 4 };
        }).sort((a, b) => a.prioridad - b.prioridad);
        setMetodosPago(_metodosDePago);
        setBancos(_bancos);
        setTiposDeReferenciasDePago(_tiposDeReferencia);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const padronToString = async (padron_id, tipo_de_padron) => {
    const response = await gettersPadronesCajaMap[tipo_de_padron]({ id: padron_id });
    const padron = response?.[0] ?? response?.results?.[0];
    return padronesToString(padron, tipo_de_padron, true);
  };

  const onFinishSearch = async () => {
    if (searchRef.current) {
      clearTimeout(searchRef.current);
    }
    searchRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        await form.validateFields();
        const params = form.getFieldsValue();
        params.estado_del_folio = permitirCobro ? 1 : 2;
        const _op = await postConsultaFolio({ folio: params.folio });
        const _ordenDePago = await getOrdenDePago(params);
        if (!_ordenDePago) {
          message.info(permitirCobro
            ? 'No se encontro referencia de pago'
            : 'No se encontro referencia de pago saldada');
          setLoading(false);
          return;
        }

        const _dataSource = { ..._ordenDePago, cargos: _op?.cargos || _ordenDePago?.cargos };
        const [{ tramite }] = _dataSource.cargos
          .map((c) => c.tramite?.id).every((t, _, [_t]) => t === _t) ? _dataSource.cargos : [{}];
        const tramites_bloqueando_adeudos = _op?.tramites_bloqueando_adeudos || [];
        _ordenDePago.disabled = tramites_bloqueando_adeudos.length
          && !tramites_bloqueando_adeudos.includes(tramite?.id);
        if (!permitirCobro && _ordenDePago) {
          const _recibos = await getRecibos({ referencia_de_pago: _ordenDePago.id });
          if (_recibos.count > 1) {
            const promises = _recibos.results.map((recibo) => getRecibosPDF([recibo.id]));
            const recibosPDF = await Promise.all(promises);
            const recibos = _recibos.results.map((recibo, idx) => ({
              ...recibo,
              base64: recibosPDF[idx].base64,
            }));
            _dataSource.recibos = recibos;
          } else if (_recibos.count === 1) {
            const _recibo = await getRecibo(_recibos?.results[0]?.id);
            _ordenDePago.folio_recibo = _recibo?.folio;
            _ordenDePago.total_recibo = _recibo?.importe_total;
          }
        }
        if (_ordenDePago) {
          const respuestaStr = _ordenDePago.respuesta.replace(/"'/g, "'").replace(/'"/g, "'").replace(/'/g, '"').replace(/None/g, 'null');
          const respuestaObj = JSON.parse(respuestaStr);
          // 24010130165
          const datosPadron = await padronToString(
            _ordenDePago.padron_id,
            _ordenDePago.tipo_de_padron,
          );
          let _total = 0;
          const _cargos = debtsFormatter(_dataSource.cargos);

          if (_cargos.some((e) => (e.convenios_id || e.convenios))) {
            const allConveniosIds = [...new Set(_cargos
              .map((e) => (e.convenios_id || e.convenios.id)))];
            const promises = allConveniosIds.map((c) => getConvenio(c));
            const _convenios = await Promise.all(promises);
            const promisesParcialidades = _convenios
              .map((c) => getParcialidades({ convenio: c.id }));
            const parcialidades = await Promise.all(promisesParcialidades);
            const convenios = _convenios.map((c, idx) => ({
              ...c,
              parcialidades: parcialidades[idx].filter((p) => p.convenio === c.id && !p.saldado),
            }));
            setConvenio(convenios[0]);
            _total = parseFloat(respuestaObj.Adeudo);
          } else {
            _total = _cargos.reduce((accum, curr) => accum + curr.adeudo_total, 0);
          }
          const importeConsulta = round(permitirCobro ? _total : (_ordenDePago?.total_recibo || 0));

          setData({
            ..._ordenDePago,
            respuesta: respuestaObj,
          });
          setDataSource(_ordenDePago);
          setDescriptionItems([
            !permitirCobro && _ordenDePago.folio_recibo && {
              key: 'folio',
              label: 'Folio del recibo',
              value: _ordenDePago.folio_recibo,
            },
            {
              key: 'importe',
              label: `Importe  ${permitirCobro ? 'a Pagar' : 'Pagado'}`,
              value: currencyFormater(importeConsulta),
            },
            {
              key: 'padron',
              label: 'Padrón',
              value: datosPadron,
            },
            {
              key: 'es_orden_de_pago',
              label: 'Orden de Pago o Trámite',
              value: _ordenDePago.es_orden_de_pago ? 'Orden de pago' : 'Trámite',
            },
          ].filter((e) => e));
        }
      } catch (err) {
        onError(err, setLoading);
      } finally {
        setLoading(false);
      }
    }, 500);
  };
  const getTotal = () => {
    const _total = parseFloat(convenio ? data.respuesta.Adeudo : data.cargos
      .reduce((accum, curr) => accum + curr.total_real, 0).toFixed(2));
    return round(_total);
  };

  const getTotalSinRedondeo = () => {
    const _total = parseFloat(convenio ? data.respuesta.Adeudo : data.cargos
      .reduce((accum, curr) => accum + curr.total_real, 0).toFixed(2));
    return _total;
  };

  const onCancel = () => {
    form.resetFields();
    setData(null);
    setDescriptionItems([]);
    setSelectedMetodo(entidad?.metodo_de_pago_principal);
    setConvenio(null);
  };

  const debouncePostRecibo = async (values) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      const _recibo = await postReciboConFolioDeOrden(values);
      if (_recibo) {
        history.push('/recibos-de-caja?from=registro_de_pagos');
        onCancel();
      }
      setLoading(false);
    }, 500);
  };

  const filtrarParcialidades = (parcialidades, monto) => {
    const resultado = [];
    let _monto = monto;
    parcialidades.forEach((parcialidad) => {
      if (_monto > 0) {
        resultado.push(parcialidad.id);
        _monto -= parcialidad.importe_de_la_parcialidad;
      }
    });

    return resultado;
  };

  const onFinish = async () => {
    try {
      await form.validateFields();
      const values = formDatosPago.getFieldsValue();
      // const metodo = formDatosPago.getFieldValue('metodo');
      const importe = getTotal();
      if (metodosPago.find((e) => e.id === values?.metodo)
        ?.requiere_datos_bancarios && !datosDePago) {
        setVisibleDatosDePago(true);
        return;
      }
      values.metodos_de_pago = [{
        ...datosDePago,
        metodo: values?.metodo,
        importe,
      }];

      if (convenio) {
        // const parcialidades = filtrarParcialidades(convenio
        // .parcialidades, data.respuesta.Adeudo);
        const parcialidades = filtrarParcialidades(
          convenio.parcialidades,
          parseFloat(data.respuesta.Adeudo),
        );
        values.metodos_de_pago[0].parcialidades = parcialidades;
        values.metodos_de_pago[0].importe = parseFloat(data.respuesta.Adeudo);
        values.convenio = convenio.id;
      }

      values.folio = form.getFieldValue('folio');
      values.importe_sin_redondeo = getTotalSinRedondeo();
      values.importe = importe;
      setLoading(true);
      if (pago_externo) {
        const sha_256 = await postFilePagoExterno(values);
        if (sha_256) {
          delete values.archivo;
          values.file_hex = sha_256.file_hex;
          values.file_name = sha_256.file_name;
        }
      }
      debouncePostRecibo(values);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const descripcionRender = (txt, record) => {
    const fecha = record?.fecha_de_vencimiento ? toMoment(record.fecha_de_vencimiento).format() : '';

    const fechaVencimiento = record?.fecha_de_vencimiento ? `Fecha de Vencimiento: ${fecha}` : '';

    return (
      <Tooltip title={fechaVencimiento}>
        {txt}
      </Tooltip>
    );
  };

  const columns = [
    {
      titleText: 'Periodo Fiscal',
      dataIndex: 'periodo',
      key: 'periodo',
      width: 150,
      render: (_, record) => record.tipo_de_cargo?.periodo_fiscal?.periodo,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 350,
      render: descripcionRender,
    },
    {
      titleText: 'Adeudo Total',
      dataIndex: 'total_real',
      key: 'total_real',
      render: (val) => currencyFormater(val || 0),
      align: 'right',
      width: 150,
    },
    {
      titleText: 'Importe Base',
      dataIndex: 'importe',
      key: 'importe',
      width: 150,
      align: 'right',
      render: (val) => currencyFormater(val || 0),
    },
  ];

  const handleRollback = async () => {
    setLoading(true);
    const { folio } = form.getFieldsValue();
    const { cargos } = await postCargosReferenciados(folio);
    if (!cargos?.length) {
      setLoading(false);
      message.warn('No se pudo realizar la operación');
      return;
    }
    form.submit();
    setLoading(false);
  };

  const showModalAlert = () => {
    Modal.warning({
      title: '¿Estás seguro?',
      content: 'Esta acción no se puede deshacer, ¿Deseas continuar?',
      onOk: handleRollback,
      onCancel: () => Modal.destroyAll(),
      centered: true,
      closable: true,
    });
  };

  const classNameColapse = {
    [ESTADOS_GLOBALES_MAP.ACTIVO]: '',
    [ESTADOS_GLOBALES_MAP.AUTORIZADO]: 'success',
    [ESTADOS_GLOBALES_MAP.CANCELADO]: 'danger',
  };

  return (
    <Spin spinning={loading} tip="Cargando...">
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinishSearch}
      >
        <Row gutter={10}>
          <Col xs={24} sm={24} md={16}>
            <Form.Item
              label="Folio"
              name="folio"
              rules={[{ required: true }]}
              normalize={toUpper}
            >
              <Input allowClear disabled={!!data} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item>
              <Space>
                <Button htmlType="submit" style={{ marginTop: screens.md ? 22 : 0 }} disabled={!!data}>
                  <SearchOutlined />
                  Buscar
                </Button>
                <Button
                  style={{ marginTop: screens.md ? 22 : 0 }}
                  disabled={!data}
                  onClick={onCancel}
                >
                  <DeleteOutlined />
                  Limpiar
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      {data && (
        <>
          <Descriptions
            title="Datos del Padrón"
            bordered
            size="small"
            column={{
              lg: 2,
              md: 2,
              sm: 1,
              xs: 1,
            }}
          >
            {descriptionItems.map((item) => (
              <Descriptions.Item
                key={item.key}
                label={item.label}
              >
                {item.value}
              </Descriptions.Item>
            ))}
          </Descriptions>
          <br />
          {dataSource && !permitirCobro && (
            <Collapse accordion>
              {dataSource?.recibos?.map((recibo) => (
                <Collapse.Panel
                  key={recibo.id}
                  className={classNameColapse[recibo.estados_globales]}
                  header={(
                    <Row justify="space-between">
                      <Col xs={24} sm={24} md={12}>
                        <Space>
                          <strong>Recibo: </strong>
                          {recibo.folio}
                        </Space>
                      </Col>
                      <Col xs={24} sm={24} md={12}>
                        <Space>
                          <strong>Importe: </strong>
                          {currencyFormater(recibo.importe_total)}
                        </Space>
                      </Col>
                      <Col>
                        <Space>
                          <strong>Estado: </strong>
                          {estadosGlobales
                            .find((e) => e.id === recibo.estados_globales).descripcion}
                        </Space>
                      </Col>
                    </Row>
                  )}
                >
                  <Row style={{ height: '120vh' }}>
                    <Previewer
                      modal={false}
                      base64={recibo.base64}
                    />
                  </Row>
                </Collapse.Panel>
              ))}
            </Collapse>
          )}
          <br />
          <Table
            cols={columns}
            data={data.cargos}
            rowKey="id"
            customActions={permitirCobro ? [
              {
                text: 'Regresar a versión anterior',
                icon: <SyncOutlined />,
                onClick: () => showModalAlert(),
                disabledBySelection: false,
                hasPermission: permission.regresar_cargos_version_anterior,
              },
            ] : []}
          />
          <br />
          <br />
          {permitirCobro && (
            <Form
              layout="vertical"
              onFinish={onFinish}
              form={formDatosPago}
              preserve
              initialValues={{ metodo: entidad.metodo_de_pago_principal }}
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="pago_externo"
                    label="¿Saldar pago externo?"
                  >
                    <Select trueFalse />
                  </Form.Item>
                </Col>
                {pago_externo && (
                <>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="tipo_de_referencia"
                      label="Tipo de Referencia"
                    >
                      <Select dataSource={tiposDeReferenciasDePago} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="fecha_real_de_pago"
                      label="Fecha de Pago"
                    >
                      <DatePicker
                        placeholder=""
                        format={DATE_FRONTEND_FORMAT}
                        disabledDate={(date) => date.isAfter(new Date())}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="observacion_del_pago"
                      label="Observacion del Pago"
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Uploader
                    title="Archivo a aplicar"
                    limitMB={50}
                    formItemName="archivo"
                    allowedExtensions={['xlsx', 'pdf', 'txt', 'TXT']}
                    allowPreview
                    rules={[{ required: true }]}
                  />
                </>
                )}
                {/* {!pago_externo && ( */}
                <>
                  <Col xs={24} sm={24} md={16}>
                    <Form.Item label="Método de Pago" name="metodo" rules={[{ required: true }]}>
                      <Select
                        dataSource={metodosPago}
                        onChange={(metodo) => {
                          if (metodo) {
                            setSelectedMetodo(metodo);
                            const match = metodosPago.find((e) => e.id === metodo);
                            if (match.requiere_datos_bancarios) {
                              setVisibleDatosDePago(true);
                            }
                          } else {
                            setDatosDePago();
                          }
                        }}
                        labelProp="concepto"
                        showArrow
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                </>
                {/* )} */}

                <Col xs={24} sm={24} md={8}>
                  <Form.Item>
                    <Button
                      htmlType="submit"
                      style={{ marginTop: screens.md ? 22 : 0 }}
                      disabled={!corte || data?.disabled}
                    >
                      <SaveOutlined />
                      Generar Recibo
                      {alerta}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          )}
        </>
      )}
      <DatosDePago
        selectedMetodo={selectedMetodo}
        visible={visibleDatosDePago}
        bancos={bancos}
        callback={(values) => setDatosDePago(values)}
        cancelCallback={(values) => {
          if (!values) {
            setDatosDePago();
            setVisibleDatosDePago(false);
            formDatosPago.resetFields();
          }
          setVisibleDatosDePago(false);
        }}
      />
    </Spin>
  );
};

RegistroDePagos.propTypes = {
  esConsulta: PropTypes.bool,
  permission: PropTypes.shape({
    cobrar: PropTypes.bool,
    regresar_cargos_version_anterior: PropTypes.bool,
  }).isRequired,
};

RegistroDePagos.defaultProps = {
  esConsulta: false,
};

export default RegistroDePagos;
